import * as React from 'react';
import { styled } from '@linaria/react';

import { normalize } from '@/utils';

import { getCurrentCommandStartTime } from './SelfDrawingPath.helpers';
import { Command } from './SelfDrawingPath.types';

interface Point {
  x: number;
  y: number;
}

interface Props {
  time: number;
  isVisible: boolean;
  currentCommandIndex: number;
  commands: Array<Command>;
  startFrom?: Point;
}

function Cursor({
  time,
  isVisible,
  currentCommandIndex,
  startFrom,
  commands,
}: Props) {
  const previousCommandIndex = currentCommandIndex - 1;

  const currentCommand = commands[currentCommandIndex];
  const previousCommand = commands[previousCommandIndex] ?? {
    type: 'move',
    to: startFrom ?? { x: 0, y: 0 },
  };
  const nextCommand = commands[currentCommandIndex + 1];

  if (!currentCommand) {
    return null;
  }

  const currentCommandStartTime = getCurrentCommandStartTime(
    time,
    commands
  );

  const progressThroughCurrentCommand =
    (time - currentCommandStartTime) / currentCommand.duration;

  const cursorX = normalize(
    progressThroughCurrentCommand,
    0,
    1,
    previousCommand.to.x,
    currentCommand.to.x
  );
  const cursorY = normalize(
    progressThroughCurrentCommand,
    0,
    1,
    previousCommand.to.y,
    currentCommand.to.y
  );

  let cursorStyles = getCursorStyles(
    currentCommand,
    currentCommandStartTime,
    previousCommand,
    nextCommand,
    time
  );

  cursorStyles = {
    ...cursorStyles,
    opacity: isVisible ? 1 : 0,
  };

  return (
    <>
      {currentCommand.type !== 'move' && (
        <CursorBackdrop
          cx={cursorX}
          cy={cursorY}
          r={0.6}
          style={{ opacity: isVisible ? 1 : 0 }}
        />
      )}
      <StyledCursor cx={cursorX} cy={cursorY} style={cursorStyles} />
    </>
  );
}

const STYLES = {
  move: {
    r: 1,
    opacity: 1,
    fillOpacity: 0.25,
  },
  line: {
    r: 0.5,
    opacity: 1,
    fillOpacity: 1,
  },
};

function getCursorStyles(
  currentCommand: Command,
  currentCommandStartTime: number,
  previousCommand: Command,
  nextCommand: Command,
  time: number
) {
  // In the final 200ms of the command, we want to start transitioning to the next command’s styles.
  const timeLeftInCurrentCommand =
    currentCommand.duration - (time - currentCommandStartTime);

  // When going from move to line, we want to start transitioning 200ms before the end of the move command.
  if (
    currentCommand.type === 'move' &&
    nextCommand?.type === 'line'
  ) {
    const isTransitioning = timeLeftInCurrentCommand < 200;

    if (isTransitioning) {
      const transitionProgress = 1 - timeLeftInCurrentCommand / 200;

      const fromStyles = STYLES[currentCommand.type];
      const toStyles = STYLES[nextCommand.type];

      return {
        r: normalize(
          transitionProgress,
          0,
          1,
          fromStyles.r,
          toStyles.r
        ),
        opacity: 1,
        fillOpacity: normalize(
          transitionProgress,
          0,
          1,
          fromStyles.fillOpacity,
          toStyles.fillOpacity
        ),
      };
    }
  }
  // When going from line to move, we want to start transitioning 200ms *after* the line command ends.
  if (
    previousCommand.type === 'line' &&
    currentCommand.type === 'move'
  ) {
    const isTransitioning = time - currentCommandStartTime < 200;

    if (!isTransitioning) {
      return STYLES[currentCommand.type];
    }

    const transitionProgress = (time - currentCommandStartTime) / 200;

    const fromStyles = STYLES[previousCommand.type];
    const toStyles = STYLES[currentCommand.type];

    return {
      r: normalize(
        transitionProgress,
        0,
        1,
        fromStyles.r,
        toStyles.r
      ),
      opacity: 1,
      fillOpacity: normalize(
        transitionProgress,
        0,
        1,
        fromStyles.fillOpacity,
        toStyles.fillOpacity
      ),
    };
  }

  return STYLES[currentCommand.type];
}

const StyledCursor = styled.circle`
  fill: hsl(45deg 100% 50%);
  transition: opacity 250ms ease-in-out;
  will-change: transform;
`;

const CursorBackdrop = styled(StyledCursor)`
  fill: var(--color-background);
`;

export default Cursor;
